import { type FlightRiskSchema } from "@ca/db/schema";
import { type RiskLevel } from "@ca/types/risk";
import { type Nullish } from "@ca/types/util";

import { resolveRotorWingRisk, type RotorWingRiskRecord } from "./rotor-wing";

export type FlightRisk = Pick<FlightRiskSchema, "reasoning" | "risk" | "type">;

export type FlightRiskResult = {
	planReason: null | string;
	planRiskLevel: null | RiskLevel;
	planRiskScore: null | number;
	riskLevel: null | RiskLevel;
	riskReason: null | string;
	riskScore: null | number;
};

type RiskType = "landing" | "route" | "takeoff";

type FlightOutcome = {
	arrivalTime?: Nullish<Date | string>;
	departureTime?: Nullish<Date | string>;
	status?: Nullish<string>;
};

export const hasFlown = (flight: FlightOutcome) =>
	flight.departureTime != null || flight.arrivalTime != null;

export const isCancelledFlight = (flight: FlightOutcome) =>
	flight.status === "scheduled_unflown" && !hasFlown(flight);

export const riskLevelToLabel = (risk: Nullish<number>): null | RiskLevel => {
	if (risk === null || risk === undefined) {
		return null;
	}
	if (risk < 40) {
		return "low";
	}
	if (risk < 70) {
		return "moderate";
	}
	return "elevated";
};

type RiskColorConfig = {
	bg: "bg-gray-400" | "bg-green-500" | "bg-red-500" | "bg-yellow-500"
	bgShade: "bg-gray-400/20" | "bg-green-500/20" | "bg-red-500/20" | "bg-yellow-500/20"
	tamgui: "$backgroundSecondary" | "$danger" | "$primary" | "$success"
	text: "text-gray-400" | "text-green-500" | "text-red-500" | "text-yellow-700"
	hex: "#ffd043" | "#25a049" | "#d30000" | "#d0d0d0"
};

export const riskLevelToColor = (level: Nullish<RiskLevel>) => {
	const RISK_COLORS: Record<"unknown" | RiskLevel, RiskColorConfig> = {
		elevated: {
			bg: "bg-red-500",
			bgShade: "bg-red-500/20",
			tamgui: "$danger",
			text: "text-red-500",
			hex: "#d30000",
		},
		low: {
			bg: "bg-green-500",
			bgShade: "bg-green-500/20",
			tamgui: "$success",
			text: "text-green-500",
			hex: "#25a049",
		},
		moderate: {
			bg: "bg-yellow-500",
			bgShade: "bg-yellow-500/20",
			tamgui: "$primary",
			text: "text-yellow-700",
			hex: "#ffd043",
		},
		unknown: {
			bg: "bg-gray-400",
			bgShade: "bg-gray-400/20",
			tamgui: "$backgroundSecondary",
			text: "text-gray-400",
			hex: "#d0d0d0",
		},
	};
	if (!level) {
		return RISK_COLORS.unknown;
	}
	return RISK_COLORS[level] ?? RISK_COLORS.unknown;
};

export const riskToColor = (risk: Nullish<number>) => {
	return riskLevelToColor(riskLevelToLabel(risk));
};


export function calculateFlightRisk (risks: FlightRisk[], rwRisk?: Nullish<RotorWingRiskRecord>): FlightRiskResult {

	let maxPlanRisk: null | number = null;
	let maxActualRisk: null | number = null;
	let planReason: null | string = null;
	let actualReason: null | string = null;

	for (const risk of risks) {
		if (risk.type.startsWith("plan-")) {
			if (maxPlanRisk === null || risk.risk > maxPlanRisk) {
				maxPlanRisk = risk.risk;
				planReason = risk.reasoning;
			}
		} else if (maxActualRisk === null || risk.risk > maxActualRisk) {
			maxActualRisk = risk.risk;
			actualReason = risk.reasoning;
		}
	}
	if (rwRisk) {
		const rotorWing = resolveRotorWingRisk(rwRisk);
		maxActualRisk = rotorWing.score;
		actualReason = rotorWing.summary;
	}

	return {
		planReason,
		planRiskLevel: riskLevelToLabel(maxPlanRisk),
		planRiskScore: maxPlanRisk,
		riskLevel: riskLevelToLabel(maxActualRisk ?? maxPlanRisk),
		riskReason: actualReason ?? planReason,
		riskScore: maxActualRisk ?? maxPlanRisk,
	};
}

export function createRiskMap (risks: FlightRisk[]): Record<RiskType, FlightRisk | null> {
	const actuals: Record<RiskType, FlightRisk | null> = {
		landing: null,
		route: null,
		takeoff: null,
	};

	const planned: Record<RiskType, FlightRisk | null> = {
		landing: null,
		route: null,
		takeoff: null,
	};
  

	for (const risk of risks) {
		if (risk.type.startsWith("plan-")) {
			planned[risk.type.substring(5) as keyof typeof planned] = risk;
		} else {
			actuals[risk.type as keyof typeof actuals] = risk;
		}
	}
	return {
		landing: actuals.landing ?? planned.landing,
		route: actuals.route ?? planned.route,
		takeoff: actuals.takeoff ?? planned.takeoff,
	};
}
